<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, ref, watch } from "vue";
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from "reka-ui";

import VoiceTranscriptUtterances from "@/components/CasePanels/RecordView/VoiceTranscript/VoiceTranscriptUtterances.vue";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { useDatapointStore } from "@/stores/datapoint-store";
import { useVoiceMachineStore } from "@/stores/voice-machine-store";

import type { CheckPointData } from "@/models/Point";
import type { DataPointCitation } from "@/models/DataPoint";
import type { TranscriptAnnotation } from "@/models/Annotation";

withDefaults(defineProps<{
  checkpoint: Pick<CheckPointData, "appliesTo" | "datapointReference"> & {
    check: Pick<CheckPointData["check"], "presenceConfidenceThreshold">;
  };
}>(), {  })

const router = useRouter();
const routeParams = useCaseRouteParams();

const dataPoint = computed(() =>
  __props.checkpoint.datapointReference == null
    ? undefined
    : useDatapointStore().datapoints[__props.checkpoint.appliesTo]?.[__props.checkpoint.datapointReference],
);

const showLowQuality = ref(false);
const status = ref<"IDLE" | "LOADING" | "ERROR" | "SUCCESS">(dataPoint.value == null ? "IDLE" : "SUCCESS");

const groupedEvidence = computed(() => {
  return (dataPoint.value?.citations ?? [])
    ?.flatMap((citation) => (citation.type === "presence" ? citation.value : []))
    .sort((a, b) => b.confidence - a.confidence)
    .reduce<Record<"good" | "bad", DataPointCitation[]>>(
      (acc, citation) => {
        const key = citation.confidence >= __props.checkpoint.check.presenceConfidenceThreshold ? "good" : "bad";
        return { ...acc, [key]: [...acc[key], citation] };
      },
      { good: [], bad: [] },
    );
});

watch(
  [() => __props.checkpoint.appliesTo, () => __props.checkpoint.datapointReference],
  async ([fileReference, datapointReference]) => {
    if (status.value === "SUCCESS" || datapointReference == null) return;

    try {
      status.value = "LOADING";
      await useDatapointStore().load({ fileReference, reference: datapointReference });
    } catch (error) {
      status.value = "ERROR";
      console.error(error);
    } finally {
      status.value = "SUCCESS";
    }
  },
  { immediate: true },
);

function goToCitation(citation: Pick<TranscriptAnnotation, "startTime" | "endTime">) {
  if (!("startTime" in citation)) return;

  const { brand, casename } = routeParams.value;

  const startTime = +citation.startTime.toFixed(2);
  const endTime = +citation.endTime.toFixed(2);

  router.push({
    name: "casefiletimerange",
    params: { brand, casename, casefile: __props.checkpoint.appliesTo, startTime, endTime },
  });

  useVoiceMachineStore().setTimeRange("check-evidence", __props.checkpoint.appliesTo, startTime, endTime);
}
</script>
<template>
  <td />
  <td colspan="100000" class="tw-border-black/5 tw-py-1.5 tw-pr-1">
    <p v-if="status === 'LOADING'" class="tw-flex tw-items-center tw-justify-start tw-gap-1">
      <i class="fas fa-spinner tw-animate-spin" />
      <span class="tw-leading-none">Loading Evidence</span>
    </p>
    <p v-else-if="status === 'ERROR'" class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-text-red-500">
      <i class="fas fa-triangle-exclamation" />
      <span class="tw-leading-none">Could not find Evidence</span>
    </p>
    <div v-else-if="status === 'SUCCESS'" class="tw-space-y-1">
      <div
        v-for="(citation, idx) in groupedEvidence.good"
        :key="`${citation.supporting}-${idx}`"
        class="tw-cursor-pointer tw-rounded-md tw-border tw-border-gray-400 tw-py-1 hover:tw-bg-black/10"
        @click="() => goToCitation(citation.contentJson)"
      >
        <VoiceTranscriptUtterances :file-reference="checkpoint.appliesTo" :list="citation.contentJson.utteranceList" />
      </div>
      <CollapsibleRoot
        v-if="groupedEvidence.bad.length > 0"
        v-model:open="showLowQuality"
        class="tw-w-full tw-space-y-1"
      >
        <CollapsibleTrigger class="tw-rounded-md tw-p-1 hover:tw-bg-gray-200 data-[state=open]:tw-bg-gray-200">
          {{ `${showLowQuality ? "Hide" : "Show"} low quality evidence (${groupedEvidence.bad.length})` }}
        </CollapsibleTrigger>
        <CollapsibleContent class="tw-ml-3 tw-space-y-1.5 tw-px-2 tw-pb-2">
          <div
            v-for="(citation, idx) in groupedEvidence.bad"
            :key="`${citation.supporting}-${idx}`"
            class="tw-cursor-pointer tw-rounded-md tw-border tw-border-gray-400 tw-py-1 hover:tw-bg-black/10"
            @click="() => goToCitation(citation.contentJson)"
          >
            <VoiceTranscriptUtterances
              :file-reference="checkpoint.appliesTo"
              :list="citation.contentJson.utteranceList"
            />
          </div>
        </CollapsibleContent>
      </CollapsibleRoot>
    </div>
  </td>
</template>
